/**
 * Vault & Loadout Management System (Phase 48)
 * 
 * Implements official Daggerheart rules:
 * - Maximum 5 domain cards in loadout
 * - Excess cards go to vault (inactive, no stat effects)
 * - Free movement between vault and loadout (stress tracking handled manually by users)
 * - Subclass/ancestry/community cards always active (separate from system)
 */

import { ContentReference } from '../types/character';

export interface LoadoutInfo {
  loadout: ContentReference[];
  vault: ContentReference[];
  loadoutCount: number;
  vaultCount: number;
  totalCount: number;
  hasSpace: boolean;
}

export interface VaultLoadoutOperation {
  type: 'move-to-loadout' | 'move-to-vault' | 'remove-completely' | 'add-to-loadout';
  cardId: string;
  autoVault?: boolean;
}

/**
 * Analyzes current loadout/vault state
 */
export const analyzeLoadoutState = (
  loadout: ContentReference[] = [],
  vault: ContentReference[] = []
): LoadoutInfo => {
  return {
    loadout,
    vault,
    loadoutCount: loadout.length,
    vaultCount: vault.length,
    totalCount: loadout.length + vault.length,
    hasSpace: loadout.length < 5
  };
};

/**
 * Checks if a card exists in loadout or vault
 */
export const findCardLocation = (
  cardId: string,
  loadout: ContentReference[] = [],
  vault: ContentReference[] = []
): 'loadout' | 'vault' | 'none' => {
  if (loadout.some(ref => ref.contentId === cardId)) return 'loadout';
  if (vault.some(ref => ref.contentId === cardId)) return 'vault';
  return 'none';
};


/**
 * Validates if a vault->loadout move is possible
 */
export const canMoveToLoadout = (
  cardId: string,
  loadout: ContentReference[] = [],
  vault: ContentReference[] = []
): { valid: boolean; reason?: string } => {
  const location = findCardLocation(cardId, loadout, vault);
  
  if (location !== 'vault') {
    return { valid: false, reason: 'Card is not in vault' };
  }
  
  if (loadout.length >= 5) {
    return { valid: false, reason: 'Loadout is full (5 cards maximum)' };
  }
  
  return { valid: true };
};

/**
 * Validates if a loadout->vault move is possible
 */
export const canMoveToVault = (
  cardId: string,
  loadout: ContentReference[] = [],
  vault: ContentReference[] = []
): { valid: boolean; reason?: string } => {
  const location = findCardLocation(cardId, loadout, vault);
  
  if (location !== 'loadout') {
    return { valid: false, reason: 'Card is not in loadout' };
  }
  
  return { valid: true };
};

/**
 * Suggests which card to move to vault when loadout is full
 * (implements "move oldest card" logic)
 */
export const suggestCardToVault = (loadout: ContentReference[]): string | null => {
  return loadout.length > 0 ? loadout[0].contentId : null;
};

/**
 * Creates a properly structured domain card reference
 */
export const createDomainCardReference = (cardId: string): ContentReference => {
  return {
    contentId: cardId,
    contentType: 'domain_card',
    quantity: 1
  };
};

